import { useState, useEffect, useCallback } from 'react';

// Communicate with the service worker using a MessageChannel for request/response.
const swRequest = (message, timeoutMs = 60000) => {
  return new Promise((resolve, reject) => {
    if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) {
      reject(new Error('Service worker not active'));
      return;
    }
    const channel = new MessageChannel();
    const timer = setTimeout(() => {
      reject(new Error('Service worker reply timeout'));
    }, timeoutMs);
    channel.port1.onmessage = (event) => {
      clearTimeout(timer);
      resolve(event.data);
    };
    navigator.serviceWorker.controller.postMessage(message, [channel.port2]);
  });
};

export const useOffline = () => {
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [isServiceWorkerReady, setIsServiceWorkerReady] = useState(false);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then(() => setIsServiceWorkerReady(true));
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Cache a single audio URL — returns true on success
  const cacheAudio = useCallback(async (audioUrl) => {
    try {
      const res = await swRequest({ type: 'CACHE_AUDIO', url: audioUrl }, 120000);
      return !!res.ok;
    } catch (e) {
      return false;
    }
  }, []);

  // Cache a batch of audio URLs. `onProgress({done,total})` invoked on each item.
  const cacheAudioBatch = useCallback(async (urls, onProgress) => {
    if (!Array.isArray(urls) || urls.length === 0) {
      return { done: 0, total: 0, success: 0 };
    }

    const handler = (event) => {
      const d = event.data;
      if (d && d.type === 'AUDIO_CACHE_PROGRESS' && typeof onProgress === 'function') {
        onProgress({ done: d.done, total: d.total });
      }
    };
    navigator.serviceWorker.addEventListener('message', handler);

    try {
      const res = await swRequest(
        { type: 'CACHE_AUDIO_BATCH', urls },
        Math.max(120000, urls.length * 20000)
      );
      return { done: res.done || 0, total: res.total || urls.length, success: res.success || 0 };
    } finally {
      navigator.serviceWorker.removeEventListener('message', handler);
    }
  }, []);

  // Query which of the provided URLs are currently cached
  const queryAudioCache = useCallback(async (urls) => {
    if (!Array.isArray(urls) || urls.length === 0) return {};
    try {
      const res = await swRequest({ type: 'QUERY_AUDIO_CACHE', urls });
      return res.cached || {};
    } catch (e) {
      return {};
    }
  }, []);

  // Clear the audio cache entirely
  const clearAudioCache = useCallback(async () => {
    try {
      const res = await swRequest({ type: 'CLEAR_AUDIO_CACHE' });
      return !!res.ok;
    } catch (e) {
      return false;
    }
  }, []);

  return {
    isOnline,
    isOffline: !isOnline,
    isServiceWorkerReady,
    cacheAudio,
    cacheAudioBatch,
    queryAudioCache,
    clearAudioCache,
  };
};
